import CryptoJS from "crypto-js";
import { SecureStringGenerator } from "./index";

export interface SavedAccount {
  id: string;
  phone_number: string;
  password: string;
  remember_password: boolean;
  last_login_time: number;
  created_time: number;
}

export enum AccountType {
  USER = 'user',
  AGENT = 'agent'
}

// Cookie存储的键名（带用户类型后缀）
const ACCOUNTS_COOKIE_KEYS = {
  [AccountType.USER]: 'user_saved_accounts_encrypted',
  [AccountType.AGENT]: 'agent_saved_accounts_encrypted'
};
// 最多存储的账号数量
const MAX_ACCOUNTS = 15;

export class AccountManager {
  // 使用固定密钥进行加密解密，确保一致性
  private static generateKey(): string {
    return 'matrix_app_secure_key';
  }

  private static encrypt(data: string): string {
    return CryptoJS.AES.encrypt(data, this.generateKey()).toString();
  }

  private static decrypt(encryptedData: string): string {
    try {
      console.log('decrypt: 开始解密数据:', encryptedData);
      const bytes = CryptoJS.AES.decrypt(
        encryptedData,
        this.generateKey()
      );
      const result = bytes.toString(CryptoJS.enc.Utf8);
      console.log('decrypt: 解密结果:', result);
      return result;
    } catch (error) {
      console.error('解密失败:', error);
      return '';
    }
  }

  // 保存账号
  static saveAccount(account: Omit<SavedAccount, 'id' | 'created_time' | 'last_login_time'>, accountType: AccountType = AccountType.USER): SavedAccount {
    console.log('saveAccount: 开始保存账号:', account, '类型:', accountType);
    const accounts = this.getAllAccounts(accountType);
    console.log('saveAccount: 当前账号列表:', accounts);
    
    // 检查账号是否已存在
    const existingIndex = accounts.findIndex(acc => acc.phone_number === account.phone_number);
    console.log('saveAccount: 账号是否已存在:', existingIndex >= 0);
    
    const newAccount: SavedAccount = {
      id: existingIndex >= 0 ? accounts[existingIndex].id : Date.now().toString(),
      phone_number: account.phone_number,
      password: account.password,
      remember_password: account.remember_password,
      last_login_time: Date.now(),
      created_time: existingIndex >= 0 ? accounts[existingIndex].created_time : Date.now()
    };
    console.log('saveAccount: 新账号:', newAccount);

    if (existingIndex >= 0) {
      // 更新现有账号
      accounts[existingIndex] = newAccount;
      console.log('saveAccount: 更新现有账号后列表:', accounts);
    } else {
      // 添加新账号
      accounts.push(newAccount);
      console.log('saveAccount: 添加新账号后列表:', accounts);
      
      // 按最后登录时间降序排序
      accounts.sort((a, b) => b.last_login_time - a.last_login_time);
      
      // 如果超过10个账号，移除最旧的（最后一个）
      if (accounts.length > MAX_ACCOUNTS) {
        const removedAccount = accounts.pop();
        console.log('saveAccount: 超过10个账号，移除最旧的:', removedAccount);
      }
    }

    this.saveAccounts(accounts, accountType);
    return newAccount;
  }

  // 删除账号
  static deleteAccount(accountId: string, accountType: AccountType = AccountType.USER): void {
    const accounts = this.getAllAccounts(accountType);
    const filteredAccounts = accounts.filter(acc => acc.id !== accountId);
    this.saveAccounts(filteredAccounts, accountType);
  }

  // 获取所有账号
  static getAllAccounts(accountType: AccountType = AccountType.USER): SavedAccount[] {
    try {
      console.log('getAllAccounts: 开始获取账号列表，类型:', accountType);
      
      // 从Cookie中获取加密数据
      const cookieKey = ACCOUNTS_COOKIE_KEYS[accountType];
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${cookieKey}=`))
        ?.split('=')[1];
      
      console.log('getAllAccounts: 从Cookie获取到的加密数据:', cookieValue);
      if (!cookieValue) {
        console.log('getAllAccounts: cookie为空，返回空数组');
        return [];
      }

      // 解码并解密数据
      const decodedCookie = decodeURIComponent(cookieValue);
      console.log('getAllAccounts: 解码后的cookie:', decodedCookie);
      const decryptedData = this.decrypt(decodedCookie);
      console.log('getAllAccounts: 解密后的数据:', decryptedData);
      if (!decryptedData) {
        console.log('getAllAccounts: 解密数据为空，返回空数组');
        return [];
      }

      const accounts = JSON.parse(decryptedData);
      console.log('getAllAccounts: 解析后的账号列表:', accounts);
      return Array.isArray(accounts) ? accounts : [];
    } catch (error) {
      console.error('获取账号列表失败:', error);
      return [];
    }
  }

  // 根据账号获取完整信息
  static getAccountByPhone(phoneNumber: string, accountType: AccountType = AccountType.USER): SavedAccount | null {
    const accounts = this.getAllAccounts(accountType);
    return accounts.find(acc => acc.phone_number === phoneNumber) || null;
  }

  // 搜索账号（用于联想功能）
  static searchAccounts(keyword: string, accountType: AccountType = AccountType.USER): SavedAccount[] {
    const accounts = this.getAllAccounts(accountType);
    if (!keyword.trim()) return accounts;

    const normalizedKeyword = keyword.toLowerCase().trim();
    return accounts.filter(acc => 
      acc.phone_number.includes(normalizedKeyword)
    ).sort((a, b) => b.last_login_time - a.last_login_time); // 按最后登录时间排序
  }

  // 保存账号列表到Cookie
  private static saveAccounts(accounts: SavedAccount[], accountType: AccountType = AccountType.USER): void {
    try {
      console.log('saveAccounts: 开始保存账号列表:', accounts, '类型:', accountType);
      const dataToEncrypt = JSON.stringify(accounts);
      console.log('saveAccounts: 要加密的数据:', dataToEncrypt);
      const encryptedData = this.encrypt(dataToEncrypt);
      console.log('saveAccounts: 加密后的数据:', encryptedData);
      
      // 保存到Cookie，设置过期时间为1年
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      const cookieKey = ACCOUNTS_COOKIE_KEYS[accountType];
      document.cookie = `${cookieKey}=${encodeURIComponent(encryptedData)}; expires=${expires}; path=/`;
      console.log('saveAccounts: 保存到Cookie成功');
    } catch (error) {
      console.error('保存账号列表失败:', error);
    }
  }

  // 迁移旧版本的单账号存储
  static migrateOldAccounts(): void {
    const encryptedData = localStorage.getItem('user_aes_key');
    
    if (!encryptedData) return;

    try {
      // 解密旧数据
      const bytes = CryptoJS.AES.decrypt(
        encryptedData,
        SecureStringGenerator.generate(16)
      );
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);

      if (decrypted) {
        const data = JSON.parse(decrypted);
        const { phone_number, password } = data;

        if (phone_number && password) {
          // 保存到新格式
          this.saveAccount({
            phone_number,
            password,
            remember_password: true
          });

          // 清理旧数据
          localStorage.removeItem('user_aes_key');
        }
      }
    } catch (error) {
      console.error('迁移旧账号数据失败:', error);
    }
  }
}